'use client';

import { type Context, createContext, useContext, useLayoutEffect, useRef, useState } from 'react';

interface FundingStage {
  title: string;
  picker: boolean;
  back?: (() => void) | undefined;
  backHref?: string | undefined;
  profile?: (() => void) | undefined;
}
type ReportStage = (stage: FundingStage) => void;
export const FundingChromeContext: Context<ReportStage | null> = createContext<ReportStage | null>(
  null,
);

export function useFundingChrome(title: string): {
  title: string;
  picker: boolean;
  hasBack: boolean;
  hasProfile: boolean;
  backHref: string | undefined;
  openProfile(): void;
  report: ReportStage;
  back(): void;
} {
  const [stage, setStage] = useState<{
    title: string;
    picker: boolean;
    hasBack: boolean;
    hasProfile: boolean;
    backHref: string | undefined;
  }>({
    title,
    picker: true,
    hasBack: false,
    hasProfile: false,
    backHref: undefined,
  });
  const profileAction = useRef<(() => void) | undefined>(undefined);
  const action = useRef<(() => void) | undefined>(undefined);
  function report(next: FundingStage): void {
    action.current = next.back;
    profileAction.current = next.profile;
    setStage((previous) =>
      previous.title === next.title &&
      previous.picker === next.picker &&
      previous.hasBack === Boolean(next.back) &&
      previous.hasProfile === Boolean(next.profile) &&
      previous.backHref === next.backHref
        ? previous
        : {
            title: next.title,
            picker: next.picker,
            hasBack: Boolean(next.back),
            hasProfile: Boolean(next.profile),
            backHref: next.backHref,
          },
    );
  }
  function back(): void {
    action.current?.();
  }
  return { ...stage, report, back, openProfile: (): void => profileAction.current?.() };
}

export function useFundingStage(
  title: string,
  picker: boolean,
  back?: () => void,
  enabled: boolean = true,
  profile?: () => void,
  backHref?: string,
): void {
  const report = useContext(FundingChromeContext);
  useLayoutEffect(() => {
    if (enabled) report?.({ title, picker, back, profile, backHref });
  }, [report, title, picker, back, enabled, profile, backHref]);
}
